import React, { useState, CSSProperties, useEffect, useRef, JSX } from 'react'
import styled, { css } from 'styled-components'

import { LoadingPlaceholder as BaseLoadingPlaceholder } from '../LoadingPlaceholder'
import { noop } from '@sketch/utils'

import { ReactComponent as ImageSlash } from '@sketch/icons/image-slash-64'
import { ReactComponent as WarningTriangle } from '@sketch/icons/warning-triangle-64'

interface ImageSizeProps {
  width?: number | string
  height?: number | string
}

export type ObjectFit = 'contain' | 'cover' | 'fill' | 'none' | 'scale-down'

export type ImageBaseProps = Pick<
  JSX.IntrinsicElements['img'],
  'alt' | 'src' | 'srcSet' | 'aria-hidden' | 'onLoad' | 'onError' | 'ref'
> & {
  zoomFactor?: number
  elementtiming?: string
  objectFit?: ObjectFit
}

const ImageBaseComponent = styled.img.withConfig({
  shouldForwardProp: (prop, defaultValidatorFn) =>
    ['elementtiming'].includes(prop) || defaultValidatorFn(prop),
})<ImageBaseProps>(
  ({ zoomFactor, objectFit = undefined }) => css`
    display: block;
    width: inherit;
    height: inherit;
    image-rendering: ${zoomFactor && zoomFactor > 1 && 'pixelated'};
    object-fit: ${objectFit};

    &[aria-hidden='true'] {
      visibility: hidden;
      opacity: 0;

      /* To avoid a jump when switching between loading and successful state */
      width: 0;
    }
  `
)

export const overlayStyle = css`
  position: absolute;
  margin: auto;
`

export const Icon = styled.svg.attrs({
  role: 'img',
})`
  ${overlayStyle}
  max-width: 64px;
  max-height: 64px;

  width: 100%;
  height: 100%;

  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  opacity: 0.3;
  padding: 4px;
`

const LoadingPlaceholder = styled(BaseLoadingPlaceholder)`
  ${overlayStyle}
`

export const ImageContainer = styled.div.attrs<ImageSizeProps>(
  ({ width, height }) => ({ style: { width, height } })
)<ImageSizeProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
`

export type ImageStatus = 'no-image' | 'loading' | 'error' | 'successful'

export interface ImageProps extends ImageSizeProps {
  alt: string
  className?: string

  customImageElement?: React.ComponentType<ImageBaseProps>
  zoomFactor?: number

  onError?: () => void
  onLoad?: (event: Event) => void

  src?: string
  srcSet?: string

  style?: CSSProperties
  loadingPlaceholder?: React.ReactNode
  objectFit?: ObjectFit
}

const Image = function Image({
  ref,
  ...props
}: ImageProps & {
  ref?: React.RefObject<HTMLImageElement | null>
}) {
  const {
    alt,
    className,
    customImageElement: ImageComponent = ImageBaseComponent,
    onError = noop,
    onLoad = noop,
    loadingPlaceholder = <LoadingPlaceholder size="24px" />,
    src,
    srcSet,
    width,
    height,
    zoomFactor,
    objectFit,
  } = props

  const imageInstance = useRef<HTMLImageElement | undefined>(undefined)
  const hasSource = !!(src || srcSet)
  const [status, setStatus] = useState<ImageStatus>(
    hasSource ? 'loading' : 'no-image'
  )

  const [currentSource, setCurrentSource] = useState<string | undefined>(
    undefined
  )

  useEffect(() => {
    if (src || srcSet) {
      const image = new window.Image()
      imageInstance.current = image

      if (src) {
        image.src = src
      }

      if (srcSet) {
        image.srcset = srcSet
      }

      image.onload = event => {
        if (!image.complete) {
          return
        }

        setStatus('successful')
        setCurrentSource(image.currentSrc)
        onLoad(event)
      }

      image.onerror = () => {
        setStatus('error')
        onError()
      }
    } else {
      setStatus('no-image')
    }
  }, [src, srcSet, onLoad, onError])

  let overlayIcon = null

  switch (status) {
    case 'error': {
      overlayIcon = <Icon as={WarningTriangle} aria-label="Error loading" />
      break
    }

    case 'no-image': {
      overlayIcon = <Icon as={ImageSlash} aria-label="No image" />
      break
    }

    case 'loading': {
      overlayIcon = loadingPlaceholder
      break
    }
  }

  const imageSize = status === 'successful' ? { width, height } : {}
  const imageHidden = status !== 'successful'

  return (
    <ImageContainer
      aria-busy={status === 'loading'}
      className={className}
      ref={ref}
      {...imageSize}
    >
      {overlayIcon}
      <ImageComponent
        aria-hidden={imageHidden}
        alt={alt}
        data-src={imageInstance.current?.currentSrc}
        src={currentSource}
        srcSet={imageInstance.current?.srcset}
        zoomFactor={zoomFactor}
        elementtiming="image"
        objectFit={objectFit}
      />
    </ImageContainer>
  )
}

export { Image, ImageBaseComponent }
